import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { STATUS_CONFIG, type Shipment } from '../../mocks/shipments';
import { fetchAllShipments } from '../../lib/shipmentsService';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'sender' | 'carrier'>('sender');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllShipments();
      setShipments(data);
    } catch {
      setError('Unable to load shipments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredShipments =
    filterStatus === 'all'
      ? shipments
      : shipments.filter((s) => s.status === filterStatus);

  const countBy = (status: string) =>
    shipments.filter((s) => s.status === status).length;

  const inTransit = countBy('in_transit');
  const outForDelivery = countBy('out_for_delivery');
  const delivered = countBy('delivered');
  const onHold =
    countBy('hold') + countBy('on_hold');

  // Derive carrier performance from real shipment data
  const carrierMap = new Map<string, { total: number; delivered: number }>();
  shipments.forEach((s) => {
    const entry = carrierMap.get(s.carrier) ?? { total: 0, delivered: 0 };
    entry.total += 1;
    if (s.status === 'delivered') entry.delivered += 1;
    carrierMap.set(s.carrier, entry);
  });
  const carriers = [...carrierMap.entries()].map(([name, v]) => ({
    name,
    shipments: v.total,
    onTime: v.total > 0 ? `${Math.round((v.delivered / v.total) * 100)}%` : '—',
  }));

  const holdShipments = shipments.filter(
    (s) => s.status === 'hold' || s.status === 'on_hold'
  );

  return (
    <div className="min-h-screen bg-[#1a1d29] font-sans">
      <Navbar />
      <main className="pt-16">
        {/* Header */}
        <div className="bg-[#1e2235] border-b border-white/5 px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="font-heading text-2xl font-bold text-white">Operations Dashboard</h1>
                <p className="text-gray-400 text-sm mt-1">Real-time overview of your shipment operations</p>
              </div>
              <div className="flex items-center gap-2">
                {(['sender', 'carrier'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap capitalize ${
                      activeTab === tab ? 'bg-[#ff6b35] text-white' : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'
                    }`}
                  >
                    {tab === 'sender' ? 'Sender View' : 'Carrier View'}
                  </button>
                ))}
                <button
                  onClick={() => navigate('/admin')}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-white/5 border border-white/10 text-gray-400 hover:text-[#ff6b35] hover:border-[#ff6b35]/20 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-settings-3-line mr-1" />
                  Admin
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-10 h-10 border-2 border-[#ff6b35] border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-400 text-sm">Loading shipments...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
              <i className="ri-error-warning-line text-4xl text-red-400" />
              <p className="text-gray-300 text-sm">{error}</p>
              <button
                onClick={load}
                className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[#ff6b35] text-white cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-1.5" />Retry
              </button>
            </div>
          ) : (
            <>
              {/* Metrics cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Total Shipments', value: shipments.length, icon: 'ri-box-3-line', color: 'text-white' },
                  { label: 'In Transit', value: inTransit, icon: 'ri-truck-line', color: 'text-[#ff6b35]' },
                  { label: 'Out for Delivery', value: outForDelivery, icon: 'ri-e-bike-2-line', color: 'text-purple-400' },
                  { label: 'Delivered', value: delivered, icon: 'ri-checkbox-circle-line', color: 'text-green-400' },
                ].map((metric) => (
                  <div key={metric.label} className="bg-[#1e2235] border border-white/5 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-400 text-xs">{metric.label}</span>
                      <div className={`w-8 h-8 flex items-center justify-center ${metric.color}`}>
                        <i className={`${metric.icon} text-xl`} />
                      </div>
                    </div>
                    <div className={`font-heading font-bold text-3xl ${metric.color}`}>{metric.value}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Shipments table */}
                <div className="lg:col-span-2 bg-[#1e2235] border border-white/5 rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
                    <h2 className="text-white font-semibold">Active Shipments</h2>
                    <div className="flex items-center gap-2">
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="bg-[#1a1d29] border border-white/10 text-gray-300 text-xs rounded-lg px-3 py-1.5 outline-none cursor-pointer"
                      >
                        <option value="all">All Status</option>
                        <option value="hold">On Hold</option>
                        <option value="picked_up">Picked Up</option>
                        <option value="in_transit">In Transit</option>
                        <option value="out_for_delivery">Out for Delivery</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </div>
                  </div>
                  {filteredShipments.length === 0 ? (
                    <div className="text-center py-16 text-gray-500">
                      <i className="ri-inbox-line text-3xl mb-3 block" />
                      <p className="text-sm">No shipments found.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/5">
                            <th className="text-left px-6 py-3 text-gray-500 text-xs font-medium">Tracking ID</th>
                            <th className="text-left px-6 py-3 text-gray-500 text-xs font-medium">Route</th>
                            <th className="text-left px-6 py-3 text-gray-500 text-xs font-medium">Status</th>
                            <th className="text-left px-6 py-3 text-gray-500 text-xs font-medium">Service</th>
                            <th className="text-left px-6 py-3 text-gray-500 text-xs font-medium">ETA</th>
                            <th className="px-6 py-3" />
                          </tr>
                        </thead>
                        <tbody>
                          {filteredShipments.map((s) => {
                            const cfg = STATUS_CONFIG[s.status];
                            return (
                              <tr key={s.id} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                                <td className="px-6 py-4">
                                  <span className="text-white font-mono text-xs">{s.id}</span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-1 text-xs text-gray-300">
                                    <span>{s.origin.city}</span>
                                    <i className="ri-arrow-right-line text-gray-500" />
                                    <span>{s.destination.city}</span>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${cfg.bg} ${cfg.color}`}>
                                    <i className={cfg.icon} />
                                    {cfg.label}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-gray-400 text-xs">{s.service}</span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-gray-400 text-xs">{s.estimatedDelivery}</span>
                                </td>
                                <td className="px-6 py-4">
                                  <button
                                    onClick={() => navigate(`/track/${s.id}`)}
                                    className="text-[#ff6b35] hover:text-[#ff8c5a] text-xs font-medium cursor-pointer whitespace-nowrap transition-colors"
                                  >
                                    Track <i className="ri-arrow-right-line" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Right panel */}
                <div className="flex flex-col gap-4">
                  {/* Quick actions */}
                  <div className="bg-[#1e2235] border border-white/5 rounded-2xl p-5">
                    <h3 className="text-white font-semibold text-sm mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { icon: 'ri-add-box-line', label: 'Create Shipment', path: '/admin', color: 'text-[#ff6b35]' },
                        { icon: 'ri-roadster-line', label: 'Live Map', path: '/live-map', color: 'text-purple-400' },
                        { icon: 'ri-route-line', label: 'Optimize Route', path: '/route-optimization', color: 'text-blue-400' },
                        { icon: 'ri-radar-line', label: 'Track Package', path: '/tracking', color: 'text-green-400' },
                      ].map((action) => (
                        <button
                          key={action.label}
                          onClick={() => navigate(action.path)}
                          className="flex flex-col items-center gap-2 p-4 bg-[#1a1d29] border border-white/5 rounded-xl hover:border-[#ff6b35]/20 transition-all cursor-pointer"
                        >
                          <div className={`w-8 h-8 flex items-center justify-center ${action.color}`}>
                            <i className={`${action.icon} text-xl`} />
                          </div>
                          <span className="text-gray-300 text-xs text-center">{action.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* On-hold alerts */}
                  <div className="bg-[#1e2235] border border-white/5 rounded-2xl p-5 flex-1">
                    <h3 className="text-white font-semibold text-sm mb-4">Attention Required</h3>
                    {holdShipments.length === 0 ? (
                      <p className="text-gray-500 text-xs">No shipments on hold. 🎉</p>
                    ) : (
                      <div className="space-y-3">
                        {holdShipments.slice(0, 5).map((s) => (
                          <div key={s.id} className="flex items-start gap-3 p-3 bg-[#1a1d29] rounded-lg">
                            <div className="w-6 h-6 flex items-center justify-center shrink-0 text-yellow-400">
                              <i className="ri-alert-line text-sm" />
                            </div>
                            <div>
                              <p className="text-gray-300 text-xs leading-relaxed">
                                <span className="font-mono text-white">{s.id}</span> — placed on hold
                              </p>
                              {s.reason && (
                                <p className="text-gray-500 text-xs mt-1 truncate max-w-[220px]">{s.reason}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Carrier performance */}
                  {activeTab === 'carrier' && (
                    <div className="bg-[#1e2235] border border-white/5 rounded-2xl p-5">
                      <h3 className="text-white font-semibold text-sm mb-4">Carrier Performance</h3>
                      {carriers.length === 0 ? (
                        <p className="text-gray-500 text-xs">No carrier data yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {carriers.map((carrier) => (
                            <div key={carrier.name} className="flex items-center justify-between p-3 bg-[#1a1d29] rounded-lg">
                              <div>
                                <p className="text-white text-xs font-medium">{carrier.name}</p>
                                <p className="text-gray-500 text-xs">{carrier.shipments} shipment{carrier.shipments !== 1 ? 's' : ''}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-green-400 text-xs font-medium">{carrier.onTime}</p>
                                <p className="text-gray-600 text-[10px] mt-0.5">completion</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}